import { useQueryClient } from "@tanstack/react-query";
import { decommaizeNumber } from "@toss/utils";
import { useState } from "react";
import { DetailTotalData } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { saveReservationPolicyAsync } from "src/api/product/product-api";
import { ReservationPolicySaveParams } from "src/api/product/product-types";
import useGetReservationPolicy, { RESERVATION_POLICY_QUERY_KEY } from "src/hooks/product/useGetReservationPolicy";
import { ContractFacilityTableData } from "src/pages/contract/commonComponents/reservation/components/ContractSpaceContents";
import { findFacilityReservationPolicy } from "src/pages/product/forms/commonSpaceForm/utils/reservationPolicy";
import LimitFrom from "src/pages/product/forms/commonSpaceForm/modal/LimitFrom";
import { SpaceTypeT } from "src/pages/product/product-types";
import { useModal } from "src/recoil/modalState/hook";
import { useToast } from "src/recoil/toast/hook";

interface Props {
  space: ContractFacilityTableData;
  isEditable: boolean;
  detailData: DetailTotalData;
  isFeeAvailable?: boolean;
}

/** 신청계약 이용한도 설정 모달 */
const ContractSpaceUsageLimitModal = ({ space, detailData, isEditable, isFeeAvailable }: Props) => {
  const serviceId = String(detailData?.contract?.contractManageId);
  const spaceType = space.commonFacilityType as SpaceTypeT;
  const { setAbstractModalZ1 } = useModal();
  const { setBaseModal } = useModal();
  const { openToast } = useToast();
  const queryClient = useQueryClient();
  const { executeAsync: saveReservationPolicy } = useApiOperation(saveReservationPolicyAsync);

  // RRVP09 로 개별 이용한도 정책 조회
  const { data: usageLimitPolicyList } = useGetReservationPolicy({ owner: { contractManageId: serviceId }, policyType: "usage-limit-policy" });

  const individualPolicy = findFacilityReservationPolicy(usageLimitPolicyList, "usage-limit-policy", space.id);
  const usageLimitPolicy = individualPolicy?.details?.usageLimitPolicy;

  const isDisabledPolicy = usageLimitPolicy?.disabled ?? true;

  const [editObj, setEditObj] = useState({
    isUnLimitReservation: isDisabledPolicy,
    isTimeEnabled: !isDisabledPolicy && usageLimitPolicy?.time ? !usageLimitPolicy.time.disabled : false,
    isMonthlyUnLimit: !isDisabledPolicy && usageLimitPolicy?.time?.monthlyLimit ? !usageLimitPolicy.time.monthlyLimit.disabled : false,
    monthlyTime: usageLimitPolicy?.time?.monthlyLimit?.minutes ?? "",
    isDailyUnLimit: !isDisabledPolicy && usageLimitPolicy?.time?.dailyLimit ? !usageLimitPolicy.time.dailyLimit.disabled : false,
    dailyTime: usageLimitPolicy?.time?.dailyLimit?.minutes ?? "",
    isFeeEnabled: !isDisabledPolicy && usageLimitPolicy?.fee ? !usageLimitPolicy.fee.disabled : false,
    isMonthlyFeeEnabled: !isDisabledPolicy && usageLimitPolicy?.fee?.monthlyLimit ? !usageLimitPolicy.fee.monthlyLimit.disabled : false,
    monthlyFee: usageLimitPolicy?.fee?.monthlyLimit?.amount ?? "",
    isDailyFeeEnabled: !isDisabledPolicy && usageLimitPolicy?.fee?.dailyLimit ? !usageLimitPolicy.fee.dailyLimit.disabled : false,
    dailyFee: usageLimitPolicy?.fee?.dailyLimit?.amount ?? "",
  });

  const handleSubmit = async () => {
    const isDisabled = editObj.isUnLimitReservation;
    const monthlyMinutes = editObj.monthlyTime.toString() === "" ? 0 : decommaizeNumber(editObj.monthlyTime.toString());
    const dailyMinutes = editObj.dailyTime.toString() === "" ? 0 : decommaizeNumber(editObj.dailyTime.toString());
    // 금액은 RRVP04/10 이 문자열로 받는다
    const monthlyAmount = String(editObj.monthlyFee.toString() === "" ? 0 : decommaizeNumber(editObj.monthlyFee.toString()));
    const dailyAmount = String(editObj.dailyFee.toString() === "" ? 0 : decommaizeNumber(editObj.dailyFee.toString()));

    const requestData: ReservationPolicySaveParams = {
      contractManageId: serviceId,
      facilityId: space.id,
      usageLimitPolicy: {
        disabled: isDisabled,
        time: {
          disabled: isDisabled || !editObj.isTimeEnabled,
          monthlyLimit: {
            disabled: isDisabled || !editObj.isTimeEnabled || !editObj.isMonthlyUnLimit,
            minutes: monthlyMinutes,
          },
          dailyLimit: {
            disabled: isDisabled || !editObj.isTimeEnabled || !editObj.isDailyUnLimit,
            minutes: dailyMinutes,
          },
        },
        fee: {
          disabled: isDisabled || !editObj.isFeeEnabled,
          monthlyLimit: {
            disabled: isDisabled || !editObj.isFeeEnabled || !editObj.isMonthlyFeeEnabled,
            amount: monthlyAmount,
          },
          dailyLimit: {
            disabled: isDisabled || !editObj.isFeeEnabled || !editObj.isDailyFeeEnabled,
            amount: dailyAmount,
          },
        },
      },
    };

    await saveReservationPolicy(requestData);
    openToast({ content: "저장 되었습니다.", isSystemToast: true });
    queryClient.invalidateQueries({ queryKey: [RESERVATION_POLICY_QUERY_KEY] });
  };

  return (
    <LimitFrom
      spaceType={spaceType}
      editObj={editObj as any}
      setEditObj={setEditObj as any}
      onClose={() => {
        setAbstractModalZ1({ isOpen: false });
      }}
      handleSubmit={() => handleSubmit()}
      isEditable={isEditable}
      isDefault={false}
      isFeeAvailable={isFeeAvailable}
    />
  );
};
export default ContractSpaceUsageLimitModal;
